import { FoodModelHistoryResponse } from "@/lib/types/order"
import { OrderDetailResponse } from "@/lib/types/order-detail"

/**
 * Fetches order history from the client-side, calling our API route handler.
 *
 * @param page - The page number to fetch.
 * @param keyword - Optional search keyword.
 * @returns Promise<FoodModelHistoryResponse>
 */
export const getOrderHistoryClient = async (
  page: number = 1,
  keyword?: string
): Promise<FoodModelHistoryResponse> => {
  const params = new URLSearchParams()
  params.set("page", page.toString())
  if (keyword) {
    params.set("keyword", keyword)
  }

  const response = await fetch(`/api/orders/history?${params.toString()}`, {
    method: "GET",
    headers: {
      // Authentication might be handled via cookies automatically by the browser.
    },
  })

  if (!response.ok) {
    // Attempt to parse error response body if available
    let errorData: FoodModelHistoryResponse | { message: string } = {
      message: `HTTP error! status: ${response.status}`,
    }
    try {
      errorData = await response.json()
    } catch (e) {
      // Ignore if response is not JSON
    }
    // Throw an error that includes the message from the API response if possible
    throw new Error(
      errorData.message || `HTTP error! status: ${response.status}`
    )
  }

  return response.json() as Promise<FoodModelHistoryResponse>
}

/**
 * Fetches details for a specific order by ID from the client-side.
 *
 * @param orderId - The ID of the order to fetch details for.
 * @returns Promise with the order details.
 */
export const getOrderDetailClient = async (
  orderId: string | number
): Promise<OrderDetailResponse> => {
  const params = new URLSearchParams()
  params.set("orderId", orderId.toString())

  const response = await fetch(`/api/orders/detail?${params.toString()}`, {
    method: "GET",
  })

  if (!response.ok) {
    // Attempt to parse error response body if available
    let errorData: { message?: string; flag?: number } = {}
    try {
      errorData = await response.json()
    } catch (e) {
      // Ignore if response is not JSON
    }
    // Throw an error that includes the message from the API response if possible
    throw new Error(
      errorData.message || `HTTP error! status: ${response.status}`
    )
  }

  return response.json() as Promise<OrderDetailResponse>
}
